import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/Card';
import {
  GraduationCap,
  TrendingUp,
  Users,
  Heart,
  Lightbulb,
  Target,
  BarChart3,
  Trophy,
  BookOpen,
  Zap,
  LineChart,
  DollarSign
} from 'lucide-react';

export default function WordleForStocksPage() {
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Stock Wordle - Our Mission",
    "description": "Making financial literacy accessible through daily stock challenges. The ultimate stock wordle and finance wordle - a guess the stock game with ticker guessing and stock trivia for beginners and pros.",
    "url": "https://wallstreetle.com/wordle-for-stocks",
    "mainEntity": {
      "@type": "Organization",
      "name": "Wallstreetle",
      "description": "Stock wordle and finance wordle - daily stock guessing game with ticker guessing for financial education and literacy. Free equity game and investing puzzle.",
      "url": "https://wallstreetle.com"
    }
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Stock Wordle</title>
        <meta
          name="description"
          content="Wallstreetle is the Stock Wordle, making financial literacy accessible through daily stock challenges. This guess the stock game features ticker guessing and stock trivia for everyone, from beginners to pros."
        />
        <meta
          name="keywords"
          content="Stock Wordle, stock wordle, finance wordle, stock guessing game, guess the stock game, ticker guessing, equity game, stock trivia, investing puzzle, daily stock challenge, stock market education, financial literacy game, learn stocks, daily stock puzzle, stock market for beginners"
        />
        <link rel="canonical" href="https://wallstreetle.com/wordle-for-stocks" />

        {/* Open Graph */}
        <meta property="og:title" content="Wallstreetle | Stock Wordle" />
        <meta
          property="og:description"
          content="The ultimate stock wordle and finance wordle! Learn about stocks through daily stock challenges. This guess the stock game with ticker guessing makes financial education fun for beginners and pros alike."
        />
        <meta property="og:url" content="https://wallstreetle.com/wordle-for-stocks" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Stock Wordle" />
        <meta
          property="twitter:description"
          content="The ultimate stock wordle and finance wordle! Learn about stocks through daily stock challenges. This guess the stock game with ticker guessing makes financial education fun for beginners and pros alike."
        />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background dark:bg-gray-900 py-12 px-4">
        <div className="max-w-5xl mx-auto">

          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-foreground">
              Stock Wordle: Making Financial Literacy Accessible
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Learn about the stock market through our daily stock challenge and investing puzzle - the Stock Wordle experience for both beginners and seasoned pros.
            </p>
          </div>

          {/* Mission Section */}
          <Card className="p-8 sm:p-10 mb-16 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-850 border-none shadow-lg">
            <div className="flex items-start gap-4">
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-primary flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">Our Mission</h2>
                <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 mb-4">
                  We believe financial literacy should be accessible, engaging, and fun for everyone.
                  Wallstreetle is the Stock Wordle - a stock wordle and finance wordle that helps you understand
                  the market through ticker guessing and stock trivia without overwhelming you with jargon or complexity.
                </p>
                <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300">
                  Whether you've never invested before or you're tracking markets daily, this equity game
                  meets you where you are and helps you grow your financial knowledge one investing puzzle at a time.
                </p>
              </div>
            </div>
          </Card>

          {/* For Beginners Section */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3 text-foreground">Perfect for Beginners</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Never invested before? No problem. Start learning today.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Lightbulb className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Guided Hints</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Get smart hints that explain key financial concepts as you play. Learn what metrics mean and why they matter.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <BookOpen className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">No Prerequisites</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Jump right in with zero prior knowledge. Our intuitive interface makes learning natural and stress-free.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <GraduationCap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Learn by Doing</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Discover how stocks work through interactive puzzles. See real charts, analyze metrics, and build intuition.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                    <Target className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Start Simple</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Begin with familiar companies in categories like Tech and Consumer. Build confidence before exploring advanced markets.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-pink-100 dark:bg-pink-900 rounded-lg">
                    <Zap className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Daily Practice</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Just 5 minutes a day builds lasting knowledge. Consistent daily puzzles help concepts stick without overwhelming you.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                    <DollarSign className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">100% Free to Start</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Access core categories and features for free. Learn at your own pace without financial barriers.
                    </p>
                  </div>
                </div>
              </Card>

            </div>
          </div>

          {/* For Pros Section */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3 text-foreground">Built for Pros</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Already tracking markets? Challenge yourself with advanced features.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Multiple Market Categories</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Master NASDAQ 100, SPY, Tech, Software, Hardware, Pharma, Financial, Consumer, Industrial, Energy, WallStreetBets, Foreign, and more.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-cyan-100 dark:bg-cyan-900 rounded-lg">
                    <LineChart className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Real Financial Metrics</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Analyze 15+ live metrics including P/E ratios, market cap, volume, and volatility. Data powered by TwelveData.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                    <Trophy className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Competitive Leaderboards</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Track your win streaks, solve times, and rankings. Compete globally or create private groups with friends.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Advanced Charts</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Study historical price movements and patterns. Sharpen your technical analysis skills with real market data.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-violet-100 dark:bg-violet-900 rounded-lg">
                    <Users className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Social Features</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Share results, challenge friends, and discuss strategies. Build a community of market enthusiasts.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-100 dark:bg-teal-900 rounded-lg">
                    <Target className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">Daily Challenges</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      New puzzles every day across all categories. Stay sharp and maintain your winning streak.
                    </p>
                  </div>
                </div>
              </Card>

            </div>
          </div>

          {/* How It Works Section */}
          <Card className="p-8 sm:p-10 mb-16 border-2 border-primary/20">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center text-foreground">How It Works</h2>
            <div className="grid gap-6 md:grid-cols-4">

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="font-bold mb-2 text-foreground">Choose Category</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Pick from multiple market categories based on your interests
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="font-bold mb-2 text-foreground">Analyze Charts</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Study real price charts and financial metrics
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="font-bold mb-2 text-foreground">Get Hints</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Unlock smart hints if you need guidance
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">4</span>
                </div>
                <h3 className="font-bold mb-2 text-foreground">Guess Company</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Make your guess - you have 4 attempts to win!
                </p>
              </div>

            </div>
          </Card>

          {/* CTA Section */}
          <div className="text-center">
            <Card className="p-10 bg-gradient-to-br from-primary to-primary/80 text-white border-none shadow-xl">
              <h2 className="text-3xl font-bold mb-4">Ready to Start Learning?</h2>
              <p className="text-lg mb-6 opacity-90">
                Join thousands of players improving their financial literacy through daily puzzles
              </p>
              <a
                href="/"
                className="inline-block px-10 py-4 bg-white text-primary rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                Play Your First Puzzle - It's Free!
              </a>
            </Card>
          </div>

        </div>
      </div>
    </>
  );
}
